import { Button, H6, InputIcon } from "@sellar-io/components";
import { TabList, TabPanel, TabPanels } from "@headlessui/react";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useDebounce } from "use-debounce";
import { ListElement, ProfilePicture, SpinnerLegacy } from "../../components";
import { useDashboardContext } from "../../components/dashboard/Dashboard.shared";
import { Description } from "../../components/description/Description";
import { ErrorMessage } from "../../components/errorMessage/ErrorMessage";
import { InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { InfiniteList } from "../../components/infiniteLoading/InfiniteList";
import { NavigationTab } from "../../components/navigationTabs/NavigationTab";
import {
  TabbedListGroup,
  TabbedListHeader,
} from "../../components/navigationTabs/TabbedListHeader";
import { ProfileImagePrompt } from "../../components/profileImageUpload/ProfileImagePrompt";
import {
  DIRECT_MESSAGE,
  ORDER_COMMENT,
  SUPPLIERS,
} from "../../constants/routes";
import { useSellarUser } from "../../hooks";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { useMessageInboxList } from "../../hooks/api/v2/message/useMessageInboxList";
import { useOrderCommentList } from "../../hooks/api/v2/orderComment/useOrderCommentList";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { useUser } from "../../hooks/api/v2/user/useUser";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { MessageTabType, useMessageTabSelect } from "./useMessageTabSelect";

const DirectMessageList = ({
  selected,
  searchValue,
}: {
  selected: boolean;
  searchValue: string;
}) => {
  const [debouncedSearchValue] = useDebounce(searchValue, 500);
  const { user } = useSellarUser();
  const relationshipId = useParamAsNumber(
    DIRECT_MESSAGE,
    "relationshipId",
    false,
  );
  const { business } = useSellarCurrentBusiness();
  const navigate = useNavigate();
  const { isTwoPanel } = useDashboardContext();

  const messageInboxListQuery = useMessageInboxList({
    limit: 10,
    search: debouncedSearchValue,
  });
  const userQuery = useUser(parseInt(user?.id.toString() ?? ""), {
    enabled: !!user?.id,
  });

  const mostRecentMessage = messageInboxListQuery.data?.pages[0]?.[0];

  // if two page split, show most recent message
  useEffect(() => {
    if (selected && isTwoPanel && mostRecentMessage && !relationshipId) {
      navigate(
        DIRECT_MESSAGE.href({
          relationshipId: mostRecentMessage.RelationshipId,
        }),
      );
    }
  }, [relationshipId, isTwoPanel, mostRecentMessage, navigate, selected]);

  return (
    <div>
      {userQuery.data ? <ProfileImagePrompt user={userQuery.data} /> : null}
      {messageInboxListQuery.isError ? (
        <ErrorMessage>
          {apiV2AssertError(messageInboxListQuery.error)?.message}
        </ErrorMessage>
      ) : messageInboxListQuery.isLoading ? (
        <SpinnerLegacy />
      ) : (
        <div>
          {messageInboxListQuery.data?.pages[0]?.length ? (
            <InfiniteList query={messageInboxListQuery}>
              {messageInboxListQuery.data.pages.map((page) =>
                page.map((message) => (
                  <ListElement
                    key={message.id}
                    to={DIRECT_MESSAGE.href({
                      relationshipId: message.RelationshipId,
                    })}
                    navigation="child"
                    selected={message.RelationshipId === relationshipId}
                  >
                    {business?.Supplier ? (
                      <ProfilePicture
                        name={message.Relationship.Retailer.Business.name}
                        logoUrl={message.Relationship.Retailer.Business.logoUrl}
                      />
                    ) : (
                      <ProfilePicture
                        name={message.Relationship.Supplier.Business.name}
                        logoUrl={message.Relationship.Supplier.Business.logoUrl}
                      />
                    )}

                    <div>
                      <span className="title">
                        {business?.Supplier
                          ? message.Relationship.Retailer.Business.name
                          : message.Relationship.Supplier.Business.name}
                      </span>
                      <H6 className={"line-clamp-2"}>
                        {message.BusinessUser.User.id == user?.id
                          ? `${message.BusinessUser.User.name} (You) - `
                          : `${message.BusinessUser.User.name} - `}
                        {dayjs(message.createdAt).fromNow()}: {message.message}
                      </H6>
                    </div>
                  </ListElement>
                )),
              )}
            </InfiniteList>
          ) : (
            <div
              className={"flex flex-col items-center gap-y-2 p-6 text-center"}
            >
              {business?.Retailer ? (
                <>
                  <div>
                    <b>
                      Get connected with suppliers on Sellar to send them a
                      message.
                    </b>
                  </div>
                  <Description>Your chat history will appear here.</Description>
                  <Button onClick={() => navigate(SUPPLIERS)}>
                    Search for a supplier
                  </Button>
                </>
              ) : (
                "No messages"
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const OrderCommentList = ({
  selected,
  searchValue,
}: {
  selected: boolean;
  searchValue: string;
}) => {
  const [debouncedSearchValue] = useDebounce(searchValue, 500);
  const orderId = useParamAsNumber(ORDER_COMMENT, "orderId", false);
  const { isTwoPanel } = useDashboardContext();
  const { user } = useSellarUser();
  const { business } = useSellarCurrentBusiness();
  const navigate = useNavigate();
  const userQuery = useUser(parseInt(user?.id.toString() ?? ""), {
    enabled: !!user?.id,
  });

  const orderCommentListQuery = useOrderCommentList({
    limit: 10,
    sortBy: "createdAt",
    sortDir: "desc",
    search: debouncedSearchValue,
    select: {
      type: "distinct",
      property: "OrderId",
    },
  });

  const mostRecentOrderComment = orderCommentListQuery.data?.pages[0]?.[0];

  // if two page split, show most recent order comment
  useEffect(() => {
    if (selected && isTwoPanel && mostRecentOrderComment && !orderId) {
      navigate(ORDER_COMMENT.href({ orderId: mostRecentOrderComment.OrderId }));
    }
  }, [selected, isTwoPanel, mostRecentOrderComment, navigate, orderId]);

  return (
    <div>
      {userQuery.data ? <ProfileImagePrompt user={userQuery.data} /> : null}

      {orderCommentListQuery.isError ? (
        <ErrorMessage>
          {apiV2AssertError(orderCommentListQuery.error)?.message}
        </ErrorMessage>
      ) : orderCommentListQuery.isLoading ? (
        <SpinnerLegacy />
      ) : (
        <div>
          {orderCommentListQuery.data?.pages[0]?.length ? (
            <InfiniteList query={orderCommentListQuery}>
              {orderCommentListQuery.data.pages.map((page) =>
                page.map((comment) => (
                  <ListElement
                    key={comment.id}
                    to={ORDER_COMMENT.href({ orderId: comment.OrderId })}
                    selected={comment.OrderId === orderId}
                  >
                    {business?.Supplier ? (
                      <ProfilePicture
                        name={comment.RetailerBusiness.name}
                        logoUrl={comment.RetailerBusiness.logoUrl}
                      />
                    ) : (
                      <ProfilePicture
                        name={comment.SupplierBusiness.name}
                        logoUrl={comment.SupplierBusiness.logoUrl}
                      />
                    )}

                    <div>
                      <span className="title">
                        {business?.Supplier
                          ? comment.RetailerBusiness.name
                          : comment.SupplierBusiness.name}
                      </span>

                      <H6 className={"line-clamp-2"}>
                        Order #{comment.OrderId}
                      </H6>
                      <H6 className={"line-clamp-2"}>
                        {comment.BusinessUser.User.id == user?.id
                          ? `${comment.BusinessUser.User.name} (You) - `
                          : `${comment.BusinessUser.User.name} - `}
                        {dayjs(comment.createdAt).fromNow()}: {comment.message}
                      </H6>
                    </div>
                  </ListElement>
                )),
              )}
            </InfiniteList>
          ) : (
            <div
              className={"flex flex-col items-center gap-y-2 p-6 text-center"}
            >
              {business?.Retailer ? (
                <>
                  <div>
                    <b>
                      Get connected with suppliers on Sellar to send them a
                      message.
                    </b>
                  </div>
                  <Description>
                    Your order comment history will appear here.
                  </Description>
                  <Button onClick={() => navigate(SUPPLIERS)}>
                    Search for a supplier
                  </Button>
                </>
              ) : (
                "No messages"
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const messageTabAsIndex = (tab: MessageTabType) => {
  switch (tab) {
    case "Order comments":
      return 1;

    default:
    case "Direct messages":
      return 0;
  }
};

const indexAsMessageTab = (index: number) => {
  switch (index) {
    default:
    case 0:
      return "Direct messages";
    case 1:
      return "Order comments";
  }
};

export const MessageTypeList = () => {
  const [selectedTab, setSelectedTab] = useMessageTabSelect();
  const activeTab = selectedTab ?? "Direct messages";
  const [searchValues, setSearchValues] = useState({
    "Direct messages": "",
    "Order comments": "",
  });
  const searchValue = searchValues[activeTab];

  return (
    <TabbedListGroup
      selectedIndex={messageTabAsIndex(selectedTab)}
      onChange={(index) => setSelectedTab(indexAsMessageTab(index))}
    >
      <TabbedListHeader>
        <TabList className="flex divide-x">
          <NavigationTab className="!h-10">Direct messages</NavigationTab>
          <NavigationTab className="!h-10">Order comments</NavigationTab>
        </TabList>
        <div className="flex w-full items-center p-4 pb-2">
          <InputContainer className="w-full">
            <InputIcon icon={"MAGNIFY"} />
            <InputText
              required={false}
              placeholder="Search..."
              value={searchValue}
              onChange={(event) =>
                setSearchValues((currentValues) => ({
                  ...currentValues,
                  [activeTab]: event.target.value,
                }))
              }
            />
          </InputContainer>
        </div>
      </TabbedListHeader>
      <TabPanels>
        <TabPanel>
          <DirectMessageList
            selected={selectedTab === "Direct messages"}
            searchValue={searchValues["Direct messages"]}
          />
        </TabPanel>
        <TabPanel>
          <OrderCommentList
            selected={selectedTab === "Order comments"}
            searchValue={searchValues["Order comments"]}
          />
        </TabPanel>
      </TabPanels>
    </TabbedListGroup>
  );
};
